import { useCallback, useMemo, useState } from "react"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { DeleteIcon, DownloadIcon, ModeEditIcon } from "@l2r-front/l2r-icons"
import { useNetworksStateContext, useRoleIsReadOnly } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useAlertsDispatchContext } from "@l2r-front/l2r-query"
import { List, Typography } from "@l2r-front/l2r-ui"

import { DocumentEditModal } from "../../components/DocumentEditModal"
import { DocumentListItem } from "../../components/DocumentListItem"
import { DocumentReaderModal } from "../../components/DocumentReaderModal"
import { I18N_NAMESPACE } from "../../constants/i18n"
import { API_MODULE_DOCUMENT } from "../../constants/modules"
import { DocumentEditForm } from "../../forms/DocumentEditForm"
import { useDocumentDelete } from "../../hooks/mutations/useDocumentDelete"
import { useDocumentTypes } from "../../hooks/queries/useDocumentTypes"
import { downloadDocument } from "../../utils/documentUtils"

import * as Styled from "./DocumentList.styled"

export const DocumentList = (props) => {
    const {
        className,
        documents,
        title,
        displayTitle = true,
        displayLoading = false,
        emptyText,
    } = props

    const [selectedDocument, setSelectedDocument] = useState(null)
    const [readerModalOpen, setReaderModalOpen] = useState(false)
    const [updateModalOpen, setUpdateModalOpen] = useState(false)

    const { selectedNetwork } = useNetworksStateContext()
    const readOnly = useRoleIsReadOnly()
    const { setAlert } = useAlertsDispatchContext()

    const {
        data: documentTypes,
        isLoading: isDocumentTypesLoading,
        isError: isDocumentTypesError,
    } = useDocumentTypes()

    const { t } = useTranslation(I18N_NAMESPACE)

    const deleteMutation = useDocumentDelete(selectedDocument, {}, {
        onSuccess: () => {
            setSelectedDocument(null)
            setReaderModalOpen(false)
        },
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.documentList.edit.delete.error"),
        successSnackbarMessage: t(I18N_NAMESPACE, "containers.documentList.edit.delete.success"),
    })

    const sortedDocuments = useMemo(() => {
        if (!documents) {
            return null
        }
        return documents.sort((a, b) => {
            const dateA = new Date(a.date)
            const dateB = new Date(b.date)
            if (dateB > dateA) {
                return 1
            }
            if (dateB < dateA) {
                return -1
            }
            return a.filename.localeCompare(b.filename, undefined, { numeric: true })
        })
    }, [documents])

    const handleDocumentClick = useCallback((document) => {
        setSelectedDocument(document)
        setReaderModalOpen(true)
    }, [])

    const handleDownloadDocument = useCallback(async (document) => {
        await downloadDocument(document)
    }, [])

    const handleEditDocument = useCallback((document) => {
        setSelectedDocument(document)
        setUpdateModalOpen(true)
    }, [])

    const handleDeleteDocument = useCallback((document) => {
        setSelectedDocument(document)
        setAlert({
            alertTitle: t(I18N_NAMESPACE, "containers.documentList.delete.confirmation"),
            cancelText: t(I18N_NAMESPACE, "containers.documentList.delete.cancel"),
            confirmText: t(I18N_NAMESPACE, "containers.documentList.delete.confirm"),
            callback: () => {
                deleteMutation.mutate()
            },
            severity: "warning",
        })
    }, [setAlert, t, deleteMutation])

    const documentItems = useMemo(() => {
        if (!sortedDocuments || !documentTypes) {
            return []
        }
        return sortedDocuments.map((document) => {
            return <DocumentListItem
                key={document.uuid}
                document={document}
                documentTypes={documentTypes}
                onClick={handleDocumentClick}
                menuConfig={[
                    {
                        enabled: true,
                        id: "download-document",
                        label: t(I18N_NAMESPACE, "containers.documentList.itemMenu.download"),
                        icon: <DownloadIcon color="objects/object-black" />,
                        props: {
                            onClick: () => handleDownloadDocument(document),
                        },
                    },
                    {
                        enabled: !readOnly,
                        id: "edit-document",
                        label: t(I18N_NAMESPACE, "containers.documentList.itemMenu.edit"),
                        icon: <ModeEditIcon color="objects/object-black" />,
                        props: {
                            onClick: () => handleEditDocument(document),
                        },
                    },
                    {
                        enabled: !readOnly,
                        id: "delete-document",
                        label: t(I18N_NAMESPACE, "containers.documentList.itemMenu.delete"),
                        icon: <DeleteIcon color="objects/object-black" />,
                        props: {
                            onClick: () => handleDeleteDocument(document),
                        },
                    },
                ]} />
        })
    }, [
        sortedDocuments,
        documentTypes,
        handleDocumentClick,
        handleEditDocument,
        handleDownloadDocument,
        handleDeleteDocument,
        readOnly,
        t])

    if (!selectedNetwork || !selectedNetwork.modules.includes(API_MODULE_DOCUMENT)) {
        return null
    }

    const showLoading = displayLoading || isDocumentTypesLoading

    return (
        <>
            <Styled.Container className={className}>
                {displayTitle && <Typography variant="H3">{title || t(I18N_NAMESPACE, "containers.documentList.title")}</Typography>}
                {isDocumentTypesError && <Styled.ErrorText>{t(I18N_NAMESPACE, "containers.documentList.error")}</Styled.ErrorText>}
                {!showLoading && !documents?.length && <Styled.EmptyText variant={"Regular"}>
                    {emptyText || t(I18N_NAMESPACE, "containers.documentList.emptyText")}
                </Styled.EmptyText>}
                {showLoading && <Styled.Skeleton />}
                {!isDocumentTypesError && !showLoading && !!documents?.length && <List>
                    {documentItems}
                </List>}
            </Styled.Container>

            {!!selectedDocument && readerModalOpen && (
                <DocumentReaderModal
                    document={selectedDocument}
                    open={!!readerModalOpen}
                    onClose={() => setReaderModalOpen(false)}
                    onDelete={handleDeleteDocument}
                    onEdit={handleEditDocument}
                />
            )}
            {!!selectedDocument && updateModalOpen && (
                <DocumentEditModal
                    document={selectedDocument}
                    mode="update"
                    FormComponent={DocumentEditForm}
                    open={!!updateModalOpen}
                    onClose={() => setUpdateModalOpen(false)}
                />
            )}
        </>
    )
}

DocumentList.propTypes = {
    className: PropTypes.string,
    documents: PropTypes.arrayOf(PropTypes.object),
    title: PropTypes.string,
    emptyText: PropTypes.string,
}